import {
  RUTERO_BOOKING_LEG_PICKUP_STATUSES,
  ruteroPickupReconfirmation,
  ruteroPickupTimeVerification,
  RuteroPickupTimeVerification,
} from '@bandido/shared/types';

/**
 * La hora de recogida de UN tramo de traslado, ya decidida para dibujar.
 *
 * Vive acá y no en `bookingLegs.ts` porque el hub publica el MISMO bloque
 * (`transferPickupSchema`) en los dos lados —`legs[].pickup` de la oferta y
 * del booking— y el contrato lo dice textual: "la MISMA definicion que
 * publica la oferta". Una sola normalización para los dos (§21.22): si el
 * asesor ve una hora antes de reservar y otra después, es un bug.
 *
 * Solo existen las caras que el contrato manda a mostrar:
 *
 * - `resolved`: el proveedor resolvió la hora del conductor — se muestra tal
 *   cual, como HORA LOCAL del punto (cero conversiones), con la zona IANA al
 *   lado cuando quien la tiene la pasa. La oferta NO publica zona; el booking
 *   sí, vía `legs[].start.timeZone`.
 * - `reconfirm`: el proveedor todavía no la da — un aviso de "a reconfirmar",
 *   el día si lo publicó, y CÓMO se reconfirma (`minHoursBefore`, `url`), que
 *   el contrato identifica como el aviso operativo que hay que mostrar. Nada
 *   de horas inventadas, y nada de inventar la antelación ni el link cuando el
 *   proveedor no los manda.
 *
 * `timeVerification` se muestra independientemente de la disponibilidad de
 * hora, incluso con `unpublished`. Sin hora ni obligación no se dibuja nada.
 */
export type TransferPickupView = (
  | { status: 'resolved' | 'unpublished'; date?: string; time?: string; timeZone?: string }
  | {
    status: 'reconfirm';
    date?: string;
    /** Ventana legacy de consulta, no plazo mínimo para gestionarla. */
    minHoursBefore?: number;
    /** Dónde reconfirma el pasajero. Absoluta y con esquema, o ausente. */
    url?: string;
  }) & { timeVerification?: RuteroPickupTimeVerification };

const PICKUP_STATUSES = new Set<string>(RUTERO_BOOKING_LEG_PICKUP_STATUSES);

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

/**
 * El bloque `pickup` del contrato → lo que se dibuja, o `undefined` si no hay
 * nada que dibujar.
 *
 * Toma `unknown` a propósito: por el lado del booking llega ya saneado
 * (`ruteroBookingLegs`), pero por el lado de la OFERTA llega directo de la
 * respuesta de búsqueda, sin pasar por ningún saneo. Un `status` que no esté
 * en el vocabulario cerrado del contrato NO se adivina: se descarta, que es
 * lo mismo que hace el contrato cuando dice "un estado nuevo se AGREGA, el
 * consumidor nunca lo deduce de un campo vacío".
 */
export function transferPickupView(
  pickup: unknown,
  timeZone?: string,
): TransferPickupView | undefined {
  if (!pickup || typeof pickup !== 'object' || Array.isArray(pickup)) {
    return undefined;
  }
  const { status, date, time, reconfirmation } = pickup as Record<string, unknown>;
  if (typeof status !== 'string' || !PICKUP_STATUSES.has(status)) {
    return undefined;
  }
  const day = text(date);
  const timeVerification = ruteroPickupTimeVerification((pickup as Record<string, unknown>).timeVerification);
  if (status === 'unpublished') return timeVerification?.required ? { status, timeVerification } : undefined;
  if (status === 'reconfirm') {
    // El saneo de la antelación y del link es el MISMO que usa la ingesta de
    // los tramos del booking (§21.22): vive en shared, no se reescribe acá.
    const how = ruteroPickupReconfirmation(reconfirmation);
    return {
      status: 'reconfirm',
      ...(day ? { date: day } : {}),
      ...(how?.minHoursBefore !== undefined ? { minHoursBefore: how.minHoursBefore } : {}),
      ...(how?.url ? { url: how.url } : {}),
      ...(timeVerification ? { timeVerification } : {}),
    };
  }

  // Un `resolved` sin día ni hora legibles no tiene nada que decir: la fila
  // no se dibuja (campos ausentes = fila ausente, nunca una fila vacía).
  const hour = text(time);
  if (!day && !hour && !timeVerification?.required) return undefined;
  const zone = text(timeZone);
  return {
    status: 'resolved',
    ...(day ? { date: day } : {}),
    ...(hour ? { time: hour } : {}),
    ...(zone ? { timeZone: zone } : {}),
    ...(timeVerification ? { timeVerification } : {}),
  };
}
